import React, { useState, useEffect } from 'react';
import { LloroFormData, Lloro } from '../types';
import './TensionForm.css';

interface LloroFormProps {
  onSubmit: (data: LloroFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Lloro;
}

const LloroForm: React.FC<LloroFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<LloroFormData>({
    intensity: '',
    duration: '',
    trigger_cause: '',
    notes: '',
    datetime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      // Convertir fecha y hora separadas a datetime-local format
      const datetime = `${initialData.date}T${initialData.time}`;
      setFormData({
        intensity: initialData.intensity,
        duration: initialData.duration || '',
        trigger_cause: initialData.trigger_cause || '',
        notes: initialData.notes || '',
        datetime: datetime
      });
    } else {
      // Valores por defecto para nuevo registro - usar hora local del usuario
      const now = new Date();
      const currentDatetime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      setFormData(prev => ({
        ...prev,
        datetime: currentDatetime
      }));
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validar intensidad
    if (!formData.intensity) {
      newErrors.intensity = 'La intensidad es obligatoria';
    }

    // Validar fecha y hora
    if (!formData.datetime) {
      newErrors.datetime = 'La fecha y hora son obligatorias';
    }

    // Validar notas
    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Las notas no pueden exceder 500 caracteres';
    }

    // Validar causa
    if (formData.trigger_cause && formData.trigger_cause.length > 255) {
      newErrors.trigger_cause = 'La causa no puede exceder 255 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getIntensityInfo = (intensity: string): { color: string; emoji: string; description: string } => {
    switch (intensity) {
      case 'leve':
        return { color: '#10b981', emoji: '😢', description: 'Llanto suave, lágrimas ligeras' };
      case 'moderado':
        return { color: '#f59e0b', emoji: '😭', description: 'Llanto notable, con sollozos' };
      case 'fuerte':
        return { color: '#ef4444', emoji: '😩', description: 'Llanto intenso, muy emotivo' };
      default:
        return { color: '#6b7280', emoji: '💧', description: '' };
    }
  };

  const handleInputChange = (field: keyof LloroFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar errores del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convertir datetime a date y time separados para el backend
      const [date, time] = formData.datetime?.split('T') || ['', ''];
      const submitData: LloroFormData = {
        intensity: formData.intensity as 'leve' | 'moderado' | 'fuerte',
        duration: formData.duration || undefined,
        trigger_cause: formData.trigger_cause || undefined,
        notes: formData.notes || undefined,
        date: date,
        time: time
      };

      await onSubmit(submitData);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Error al guardar el lloro' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mostrar info de intensidad si está seleccionada
  const intensityInfo = formData.intensity ? getIntensityInfo(formData.intensity) : null;

  return (
    <div className="tension-form">
      <form onSubmit={handleSubmit} className="form">
        {errors.submit && (
          <div className="error-message">
            {errors.submit}
          </div>
        )}

        {/* Intensidad del lloro */}
        <div className="form-section">
          <h3 className="section-title">😢 Registro de Lloro</h3>

          {/* Fecha y Hora */}
          <div className="form-field">
            <label htmlFor="datetime" className="form-label">
              📅 Fecha y hora *
            </label>
            <input
              type="datetime-local"
              id="datetime"
              className={`form-input ${errors.datetime ? 'error' : ''}`}
              value={formData.datetime}
              onChange={(e) => handleInputChange('datetime', e.target.value)}
              required
            />
            {errors.datetime && <span className="field-error">{errors.datetime}</span>}
          </div>

          {/* Intensidad */}
          <div className="form-field">
            <label className="form-label">💧 Intensidad *</label>
            <div className="intensity-options">
              {['leve', 'moderado', 'fuerte'].map((intensity) => {
                const info = getIntensityInfo(intensity);
                return (
                  <button
                    key={intensity}
                    type="button"
                    className={`intensity-option ${formData.intensity === intensity ? 'selected' : ''}`}
                    onClick={() => handleInputChange('intensity', intensity)}
                    style={{
                      borderColor: formData.intensity === intensity ? info.color : undefined,
                      backgroundColor: formData.intensity === intensity ? `${info.color}15` : undefined
                    }}
                  >
                    <span className="intensity-emoji">{info.emoji}</span>
                    <span className="intensity-label" style={{ color: formData.intensity === intensity ? info.color : undefined }}>
                      {intensity.charAt(0).toUpperCase() + intensity.slice(1)}
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.intensity && <span className="field-error">{errors.intensity}</span>}
          </div>

          {/* Info de intensidad */}
          {intensityInfo && (
            <div className="tension-category" style={{ borderLeftColor: intensityInfo.color }}>
              <div className="category-info">
                <span className="category-name" style={{ color: intensityInfo.color }}>
                  {intensityInfo.emoji} {formData.intensity?.charAt(0).toUpperCase()}{formData.intensity?.slice(1)}
                </span>
                <span className="category-description">{intensityInfo.description}</span>
              </div>
            </div>
          )}
        </div>

        {/* Duración */}
        <div className="form-field">
          <label htmlFor="duration" className="form-label">
            ⏱️ Duración (opcional)
          </label>
          <select
            id="duration"
            className="form-input"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="menos de 1 minuto">Menos de 1 minuto</option>
            <option value="1-5 minutos">1-5 minutos</option>
            <option value="5-15 minutos">5-15 minutos</option>
            <option value="15-30 minutos">15-30 minutos</option>
            <option value="30-60 minutos">30-60 minutos</option>
            <option value="más de 1 hora">Más de 1 hora</option>
          </select>
        </div>

        {/* Causa/Motivo */}
        <div className="form-field">
          <label htmlFor="trigger_cause" className="form-label">
            🎯 Causa o motivo (opcional)
          </label>
          <input
            type="text"
            id="trigger_cause"
            className={`form-input ${errors.trigger_cause ? 'error' : ''}`}
            value={formData.trigger_cause}
            onChange={(e) => handleInputChange('trigger_cause', e.target.value)}
            placeholder="Ej: estrés, tristeza, película, alegría..."
            maxLength={255}
          />
          {errors.trigger_cause && <span className="field-error">{errors.trigger_cause}</span>}
        </div>

        {/* Notas */}
        <div className="form-field">
          <label htmlFor="notes" className="form-label">
            📝 Notas (opcional)
          </label>
          <textarea
            id="notes"
            className={`form-textarea ${errors.notes ? 'error' : ''}`}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Cómo te sentiste, qué lo provocó, cómo te sentiste después..."
            rows={3}
            maxLength={500}
          />
          <div className="character-count">
            {formData.notes?.length || 0}/500 caracteres
          </div>
          {errors.notes && <span className="field-error">{errors.notes}</span>}
        </div>

        {/* Botones */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                {initialData ? 'Actualizando...' : 'Guardando...'}
              </>
            ) : (
              <>
                <span className="btn-icon">💾</span>
                {initialData ? 'Actualizar Lloro' : 'Guardar Lloro'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LloroForm;
